import { useCallback, useEffect, useRef, useState } from "react";

/** Three 15-second server keepalive intervals without receiving stream bytes. */
export const STREAM_IDLE_RECONCILE_MS = 45_000;
export const STREAM_RECONCILE_REQUEST_TIMEOUT_MS = 15_000;
const STREAM_IDLE_CHECK_MS = 1_000;

export type SilentStreamReconcileResult = "finished" | "running";
export type SilentStreamRecoveryState =
  "idle" | "checking" | "reconnecting" | "error";

const finalConversationStatuses = new Set([
  "finished",
  "awaiting_input",
  "idle",
  "failed",
  "error",
  "cancelled",
]);

export function isFinalConversationSummary(summary: {
  kind: string;
  agentStatus: string;
}): boolean {
  // The list API can return ordinary chat-kind rows with a durable agent
  // status. Whether a status is terminal does not depend on the row kind.
  return finalConversationStatuses.has(summary.agentStatus);
}

export async function coalesceForcedMessagePoll(
  inFlightPoll: Promise<boolean>,
  forcePoll: (inFlightTimedOut: boolean) => Promise<boolean>,
  timeoutMs = STREAM_RECONCILE_REQUEST_TIMEOUT_MS,
): Promise<boolean> {
  // The in-flight request may have started before the reconciliation signal,
  // so wait briefly, then make one fresh request for the current transcript.
  const inFlightTimedOut = !(await waitForPromiseWithTimeout(
    inFlightPoll,
    timeoutMs,
  ));
  return forcePoll(inFlightTimedOut);
}

export async function waitForPromiseWithTimeout<T>(
  promise: Promise<T>,
  timeoutMs = STREAM_RECONCILE_REQUEST_TIMEOUT_MS,
): Promise<boolean> {
  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      promise.then(() => true),
      new Promise<boolean>((resolve) => {
        timeoutId = window.setTimeout(() => resolve(false), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

export async function fetchWithAbortTimeout<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMs = STREAM_RECONCILE_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      operation(controller.signal),
      new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => {
          controller.abort();
          reject(new Error("Conversation message refresh timed out."));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  }
}

interface UseSilentStreamRecoveryOptions {
  active: boolean;
  conversationKey: string;
  recoverySignal: number;
  reconcile: () => Promise<SilentStreamReconcileResult>;
  idleWindowMs?: number;
}

export function useSilentStreamRecovery({
  active,
  conversationKey,
  recoverySignal,
  reconcile,
  idleWindowMs = STREAM_IDLE_RECONCILE_MS,
}: UseSilentStreamRecoveryOptions) {
  const [state, setState] = useState<SilentStreamRecoveryState>("idle");
  const lastActivityAtRef = useRef(Date.now());
  const inFlightRef = useRef(false);
  const activeRef = useRef(active);
  const reconcileRef = useRef(reconcile);
  activeRef.current = active;
  reconcileRef.current = reconcile;

  const attemptReconcile = useCallback(async (force = false) => {
    if ((!force && !activeRef.current) || inFlightRef.current) return;
    inFlightRef.current = true;
    lastActivityAtRef.current = Date.now();
    setState("checking");
    try {
      const result = await reconcileRef.current();
      setState(result === "finished" ? "idle" : "reconnecting");
    } catch {
      setState("error");
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    lastActivityAtRef.current = Date.now();
    setState("idle");
  }, [conversationKey]);

  useEffect(() => {
    if (!active && state !== "reconnecting") return undefined;
    const interval = window.setInterval(() => {
      if (
        document.hidden ||
        Date.now() - lastActivityAtRef.current < idleWindowMs
      ) {
        return;
      }
      void attemptReconcile();
    }, STREAM_IDLE_CHECK_MS);
    return () => window.clearInterval(interval);
  }, [active, attemptReconcile, idleWindowMs, state]);

  useEffect(() => {
    if (recoverySignal > 0) void attemptReconcile(true);
  }, [attemptReconcile, recoverySignal]);

  const recordActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now();
    setState("idle");
  }, []);

  return {
    state,
    recordActivity,
    retry: () => attemptReconcile(true),
  };
}
